import {
  extendVariants,
  LinkProps as NextUILinkProps,
  useLink,
} from "@heroui/react";
import { PressEvent } from "@react-types/shared";
import clsx from "clsx";
import { TrackEventProps } from "core/types";
import { generateTrackName } from "core/validationSchemas/utils";
import { cloneElement, ComponentProps, isValidElement, ReactNode } from "react";
import { useTracking } from "react-tracking";
import { BUTTON_DISPLAY_NAME } from "../button/button";

type BaseLinkProps = Omit<
  NextUILinkProps,
  "showAnchorIcon" | "anchorIcon" | "color"
> & {
  /**
   * The icon to display after the text.
   */
  endIcon?: React.ReactNode;
  /**
   * ID to uniquely identify the link for tracking purposes.
   */
  id: string;
  /**
   * The icon to display before the text.
   */
  startIcon?: React.ReactNode;
  trackEventProps?: TrackEventProps;
  color?: NextUILinkProps["color"] | "primary-dark";
};

function BaseLink({
  color,
  endIcon,
  id,
  ref,
  startIcon,
  ...props
}: BaseLinkProps & { ref?: React.Ref<HTMLAnchorElement> }) {
  const getIconClone = (icon: ReactNode) =>
    isValidElement(icon)
      ? cloneElement(icon, {
          // @ts-ignore
          "aria-hidden": true,
          className: clsx({
            "group-hover:text-danger-dark": color === "danger",
            "group-hover:text-primary-dark": color === "primary",
            "group-hover:text-secondary-dark": color === "secondary",
            "group-hover:text-success-dark": color === "success",
            "group-hover:text-warning-dark": color === "warning",
            "mx-1 flex self-center leading-none text-current": true, // base classes
          }),
          height: "1em",
          stroke: "currentColor",
          width: "1em",
        })
      : null;

  const displayName = (props.as as { displayName?: string })?.displayName;
  const isButton =
    !!displayName && [BUTTON_DISPLAY_NAME, "Button"].includes(displayName);

  const handlePress = (e: PressEvent) => {
    trackEvent({
      name: generateTrackName({
        id: id,
        trackEventProps: props.trackEventProps,
        type: isButton ? "Button" : "Link",
      }),
    });
    (props.onPress as (e: PressEvent) => void)?.(e);
  };

  const startContent = getIconClone(startIcon);
  const endContent = getIconClone(endIcon);
  const { trackEvent } = useTracking();

  const { children, Component, getLinkProps } = useLink({
    ...props,
    className: clsx({
      "group w-fit rounded-xs font-bold hover:underline hover:decoration-1 hover:opacity-100":
        true,
      "h-auto! min-h-0! bg-transparent! p-0! hover:bg-transparent! data-[hover=true]:bg-transparent! data-[pressed=true]:translate-y-0! data-[pressed=true]:transform-none! hover:[&.text-danger]:text-danger hover:[&.text-primary]:text-primary hover:[&.text-secondary]:text-secondary hover:[&.text-success]:text-success hover:[&.text-warning]:text-warning":
        isButton,
      "inline-flex items-center gap-0": !!startContent || !!endContent,
      [props.className ?? ""]: true,
      "text-base": props.size === "md",
      "text-lg": props.size === "lg",
      "text-sm": props.size === "sm",
    }),
    color: color as NextUILinkProps["color"],
    onPress: handlePress,
    ref,
    role: isButton ? "button" : "link",
    ...(isButton && {
      disableAnimation: true,
      disableRipple: true,
    }),
  });

  return (
    <Component {...getLinkProps()}>
      <>
        {startContent}
        {children}
        {endContent}
      </>
    </Component>
  );
}

BaseLink.displayName = "RecareUI.BaseLink";

const ExtendedLink = extendVariants(BaseLink, {
  variants: {
    color: {
      danger: "text-danger",
      primary: "text-primary",
      "primary-dark": "text-primary-dark",
      secondary: "text-secondary",
      success: "text-success",
      warning: "text-warning",
    },
  },
});

export type LinkProps = Omit<ComponentProps<typeof ExtendedLink>, "id"> & {
  id: string;
  ref?: React.Ref<HTMLAnchorElement>;
};

export function Link({ ref, ...props }: LinkProps) {
  return <ExtendedLink {...props} ref={ref} data-testid={props.id} />;
}

Link.displayName = "RecareUI.Link";
